import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout, { Product as D } from '../components/DashboardLayout';
import { semanticTone } from '../components/customerUi';
import { ErrorState, LoadingState, PageHeader, SecondaryButton } from '../components/customerUi';
import { resolveEscrowUiState } from '../components/transactionState';
import {
  buildActionItems,
  computeProtectedValue,
  filterUserRelevantDisputes,
  getTransactionValue,
  isDisputeOpen,
  isLikelyTestTransaction,
  isUserBuyerForTransaction,
  isUserParticipantForTransaction,
  isUserSellerForTransaction,
} from './dashboardMoneyState';
import { getPayoutScheduleMessage } from '../utils/payoutSchedule';
import { trackStartTransaction } from '../utils/analytics';
import { usePlatformConfig } from '../context/PlatformConfigContext';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  Eye,
  EyeOff,
  LifeBuoy,
  Lock,
  Plus,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';

const DASHBOARD_VALUES_KEY = 'trusttrade_dashboard_show_exact_values';

const money = (value, decimals = 0) =>
  `R ${Number(value || 0).toLocaleString('en-ZA', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;

const hiddenMoney = '••••••';

const displayMoney = (value, showExactValues, decimals = 0) =>
  showExactValues ? money(value, decimals) : hiddenMoney;

function Dashboard() {
  const [user, setUser] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [walletData, setWalletData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [showExactValues, setShowExactValues] = useState(() => {
    const stored = window.sessionStorage.getItem(DASHBOARD_VALUES_KEY);
    if (stored === null) return true;
    return stored !== 'false';
  });
  const [now, setNow] = useState(() => new Date());
  const { config: platformConfig } = usePlatformConfig();
  const { needsOnboarding } = useAuth();
  const navigate = useNavigate();

  // Guide brand-new users through setup (verify phone, add banking if selling).
  // needsOnboarding is set from /auth/onboarding-status after login; the Onboarding
  // page lets them "Skip for now" (which clears the flag), so this won't loop.
  useEffect(() => {
    if (needsOnboarding) navigate('/onboarding', { replace: true });
  }, [needsOnboarding, navigate]);

  useEffect(() => {
    const updateNow = () => setNow(new Date());
    updateNow();

    const timer = window.setInterval(updateNow, 1000);
    window.addEventListener('focus', updateNow);
    document.addEventListener('visibilitychange', updateNow);

    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', updateNow);
      document.removeEventListener('visibilitychange', updateNow);
    };
  }, []);

  useEffect(() => {
    window.sessionStorage.setItem(DASHBOARD_VALUES_KEY, String(showExactValues));
  }, [showExactValues]);

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [userRes, transactionsRes, disputesRes, walletRes] = await Promise.all([
        api.get('/auth/me'),
        api.get('/transactions'),
        api.get('/disputes'),
        api.get('/wallet').catch(() => ({ data: null })),
      ]);
      setUser(userRes.data);
      setTransactions(transactionsRes.data);
      setDisputes(disputesRes.data);
      if (walletRes.data) setWalletData(walletRes.data);
    } catch (error) {
      if (error.response?.status === 401) navigate('/');
      else setLoadError('We could not load your dashboard. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { fetchDashboardData(); }, [fetchDashboardData]);

  const payoutSchedule = useMemo(() => getPayoutScheduleMessage(now, platformConfig), [now, platformConfig]);

  const userRelevantDisputes = useMemo(
    () => filterUserRelevantDisputes(disputes, transactions, user),
    [disputes, transactions, user]
  );
  const userRelevantTransactions = useMemo(
    () => transactions.filter((transaction) => isUserParticipantForTransaction(transaction, user)),
    [transactions, user]
  );
  const pendingDisputes = userRelevantDisputes.filter(isDisputeOpen);
  const activeTransactions = userRelevantTransactions.filter(t => !resolveEscrowUiState(t, pendingDisputes, now, payoutSchedule).terminal);
  const completedTransactions = userRelevantTransactions.filter(t => resolveEscrowUiState(t, pendingDisputes, now, payoutSchedule).state === 'COMPLETED');
  const actionItems = useMemo(() => buildActionItems(userRelevantTransactions, userRelevantDisputes, user, payoutSchedule), [userRelevantTransactions, userRelevantDisputes, user, payoutSchedule]);
  const recentTransactions = userRelevantTransactions.slice(0, 5);
  const totalEscrowValue = computeProtectedValue(userRelevantTransactions, pendingDisputes, now, payoutSchedule);

  const firstName = user?.name?.split(' ')[0] ?? '';
  const greeting = loading
    ? 'Loading'
    : `${new Date().getHours() < 12 ? 'Good morning' : new Date().getHours() < 18 ? 'Good afternoon' : 'Good evening'}, ${firstName}`;

  if (loading) return <DashboardLayout user={user} loading><LoadingState label="Loading your dashboard…" /></DashboardLayout>;
  if (loadError) return <DashboardLayout user={user}><PageHeader title="Dashboard" subtitle="Your transactions and next actions" /><ErrorState title="Dashboard unavailable" body={loadError} action={<SecondaryButton onClick={fetchDashboardData}>Try again</SecondaryButton>} /></DashboardLayout>;

  return (
    <DashboardLayout user={user} loading={loading} darkMain>
      <style>{`
        .tt-dash {
          max-width: 1180px;
          display: flex;
          flex-direction: column;
          gap: 18px;
          animation: vaultFadeIn 0.2s ease;
        }
        .tt-glass {
          position: relative;
          border: 1px solid ${D.border};
          border-radius: 16px;
          background: ${D.surface};
          backdrop-filter: blur(16px);
        }
        .tt-summary-row {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 12px;
        }
        .tt-secondary-row {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 12px;
        }
        .tt-btn { transition: filter 0.12s, border-color 0.12s, background 0.12s, transform 0.08s; }
        .tt-btn:hover:not(:disabled) { filter: brightness(1.08); }
        .tt-btn:active:not(:disabled) { transform: translateY(1px); }
        .tt-row-hover:hover { background: ${D.hover} !important; }
        .tt-tx-row {
          display: flex; align-items: center; gap: 14px;
          padding: 13px 10px; border-radius: 10px; cursor: pointer;
        }
        .tt-tx-main { min-width: 0; flex: 1; }
        .tt-tx-meta { display: flex; align-items: center; gap: 10px; flex-shrink: 0; }
        @media (max-width: 680px) {
          .tt-summary-row { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .tt-secondary-row { grid-template-columns: 1fr; }
        }
        @media (max-width: 560px) {
          .tt-tx-row { flex-wrap: wrap; }
          .tt-tx-main { flex: 1 1 100%; }
          .tt-tx-meta { flex: 1 1 100%; justify-content: space-between; }
          .tt-tx-arrow { display: none; }
        }
      `}</style>

      <div className="tt-dash">
        <Hero
          greeting={greeting}
          pendingDisputes={pendingDisputes}
          showExactValues={showExactValues}
          setShowExactValues={setShowExactValues}
          isAdmin={user?.is_admin}
          navigate={navigate}
        />

        {!loading && user && (!user.phone_verified || ((user.role === 'seller') && !user.banking_details_completed)) && (
          <ProfileReadiness user={user} navigate={navigate} />
        )}

        <PriorityAction actionItems={actionItems} navigate={navigate} showExactValues={showExactValues} />

        <SummaryRow
          activeCount={activeTransactions.length}
          protectedValue={totalEscrowValue}
          completedCount={completedTransactions.length}
          showExactValues={showExactValues}
        />

        <RecentTransactions
          recentTransactions={recentTransactions}
          pendingDisputes={pendingDisputes}
          navigate={navigate}
          showExactValues={showExactValues}
          user={user}
        />

        <div className="tt-secondary-row">
          <PaymentProtectionCard walletData={walletData} protectedValue={totalEscrowValue} showExactValues={showExactValues} payoutSchedule={payoutSchedule} />
          <NeedHelpCard />
        </div>
      </div>
    </DashboardLayout>
  );
}

function Hero({ greeting, pendingDisputes, showExactValues, setShowExactValues, isAdmin, navigate }) {
  const hasOpenDispute = pendingDisputes.length > 0;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
      <div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 11px', borderRadius: 999, background: hasOpenDispute ? `${D.error}1A` : `${D.success}1A`, border: `1px solid ${hasOpenDispute ? D.error : D.success}40`, marginBottom: 12 }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: hasOpenDispute ? D.error : D.success }} />
          <span style={{ color: hasOpenDispute ? D.error : D.success, fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
            {hasOpenDispute ? 'Case under review' : 'Protection online'}
          </span>
        </div>
        <h1 data-testid="dashboard-title" style={{ margin: 0, color: D.text, fontSize: 'clamp(24px, 3vw, 32px)', lineHeight: 1.2, fontWeight: 700, letterSpacing: '-0.02em' }}>
          {greeting}.
        </h1>
        <p style={{ margin: '6px 0 0', color: D.sub, fontSize: 14 }}>See exactly where your money is and what happens next.</p>
      </div>
      {isAdmin && (
        <button
          onClick={() => setShowExactValues(!showExactValues)}
          className="tt-btn"
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 8, border: `1px solid ${D.border}`, background: D.hover, color: D.sub, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
        >
          {showExactValues ? <EyeOff size={13} /> : <Eye size={13} />}
          {showExactValues ? 'Hide values' : 'Show values'}
        </button>
      )}
    </div>
  );
}

function ProfileReadiness({ user, navigate }) {
  const needsSellerBanking = user.role === 'seller' && !user.banking_details_completed;
  const phonePrompt = 'Verify your phone number to continue.';
  const bankingPrompt = 'Add banking details to receive payouts.';
  return (
    <section className="tt-card tt-glass" style={{ padding: 16, borderColor: `${D.warn}40` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <AlertCircle size={17} color={D.warn} />
          <div>
            <p style={{ margin: 0, color: D.text, fontWeight: 700, fontSize: 14 }}>Finish setting up your account</p>
            <p style={{ margin: '3px 0 0', color: D.sub, fontSize: 12 }}>
              {!user.phone_verified && needsSellerBanking
                ? `${phonePrompt} ${bankingPrompt}`
                : !user.phone_verified
                ? phonePrompt
                : bankingPrompt}
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {!user.phone_verified && <MiniButton label="Verify phone" onClick={() => navigate('/verify/phone')} />}
          {needsSellerBanking && <MiniButton label="Add banking details" onClick={() => navigate('/settings/banking')} />}
        </div>
      </div>
    </section>
  );
}

function MiniButton({ label, onClick }) {
  return (
    <button onClick={onClick} className="tt-btn" style={{ border: `1px solid ${D.warn}66`, background: `${D.warn}1F`, color: D.warn, borderRadius: 8, padding: '8px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
      {label}
    </button>
  );
}

// The single dominant next-step card — the strongest element on the page.
// Falls back to a calm "No action needed" state rather than disappearing,
// so the user always gets a clear answer to "what do I do next?".
function PriorityAction({ actionItems, navigate, showExactValues }) {
  const action = actionItems[0];

  if (!action) {
    return (
      <section className="tt-card tt-glass" style={{ padding: '24px 26px', borderColor: `${D.success}33` }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, display: 'grid', placeItems: 'center', background: `${D.success}1A`, border: `1px solid ${D.success}40`, flexShrink: 0 }}>
              <CheckCircle2 size={22} color={D.success} />
            </div>
            <div>
              <p style={{ margin: '0 0 4px', color: D.success, fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>No action needed</p>
              <h2 style={{ margin: 0, color: D.text, fontSize: 'clamp(18px, 2vw, 21px)', fontWeight: 700 }}>You're all caught up</h2>
              <p style={{ margin: '4px 0 0', color: D.sub, fontSize: 13 }}>Nothing needs your attention right now. We'll let you know when a transaction needs you.</p>
            </div>
          </div>
          <button
            onClick={() => { trackStartTransaction({ source: 'dashboard_no_action' }); navigate('/transactions/new'); }}
            className="tt-btn"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 7, border: `1px solid ${D.border}`, background: D.hover, color: D.text, borderRadius: 10, padding: '11px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}
          >
            <Plus size={14} color={D.accent} />
            New transaction
          </button>
        </div>
      </section>
    );
  }

  const { transaction } = action;
  // Hierarchy answers, top to bottom: how much + where is it (the money
  // block — the single most prominent thing on the card), what's happening
  // (status + consequence), then who needs to act and how (the next-step
  // block). See buildActionItems for moneyWord/moneyLocation/statusPlain/
  // moneyNote — business logic (which branch fires) is untouched; only this
  // presentation layer changed.
  return (
    <section className="tt-card tt-glass" style={{ padding: '24px 26px', borderColor: `${action.color}40` }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ color: D.text, fontSize: 'clamp(28px, 3.4vw, 36px)', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
          {displayMoney(action.amount, showExactValues, 0)}
        </span>
        <span style={{ color: D.text, fontSize: 'clamp(18px, 2vw, 22px)', fontWeight: 700 }}>
          {action.moneyWord}
        </span>
      </div>
      <p style={{ margin: '4px 0 0', color: D.text, fontSize: 14.5, fontWeight: 500 }}>
        {action.moneyLocation}
      </p>
      <p style={{ margin: '2px 0 0', color: D.dim, fontSize: 12 }}>
        {transaction.share_code || transaction.deal_id || transaction.transaction_id}
      </p>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16 }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: action.color, flexShrink: 0 }} />
        <span style={{ color: semanticTone(action.color), fontSize: 13, fontWeight: 700 }}>{action.statusPlain}</span>
      </div>
      <p style={{ margin: '4px 0 0', color: D.sub, fontSize: 13, lineHeight: 1.5, maxWidth: 520 }}>
        {action.moneyNote}
      </p>

      <div style={{ height: 1, background: D.border, margin: '20px 0 16px' }} />

      <p style={{ margin: '0 0 4px', color: D.dim, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {action.eyebrow}
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16, justifyContent: 'space-between' }}>
        <h2 style={{ margin: 0, color: D.text, fontSize: 'clamp(17px, 2vw, 20px)', lineHeight: 1.3, fontWeight: 700 }}>
          {action.title}
        </h2>
        <button
          onClick={() => navigate(action.path, action.path === '/disputes-dashboard' ? { state: { transactionId: transaction.transaction_id } } : undefined)}
          className="tt-btn"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            border: 'none',
            background: action.solidBg || D.accentGradient,
            color: action.textColor || '#16181B',
            borderRadius: 12, padding: '13px 22px', cursor: 'pointer',
            fontSize: 14.5, fontWeight: 700, whiteSpace: 'nowrap',
            boxShadow: D.shadow,
          }}
        >
          {action.button}
          <ArrowRight size={16} />
        </button>
      </div>
    </section>
  );
}

function SummaryRow({ activeCount, protectedValue, completedCount, showExactValues }) {
  return (
    <div className="tt-summary-row">
      <SummaryCell label="Active transactions" value={activeCount} dotColor={D.warn} caption={activeCount > 0 ? `${activeCount} in progress` : 'Nothing in progress'} />
      <SummaryCell label="Protected transaction value" value={displayMoney(protectedValue, showExactValues)} dotColor={D.success} caption="Protected with our payment provider" />
      <SummaryCell label="Completed" value={completedCount} dotColor={D.accent} caption="Finished transactions" />
    </div>
  );
}

function SummaryCell({ label, value, dotColor, caption }) {
  return (
    <div className="tt-card tt-glass" style={{ padding: '16px 18px' }}>
      <p style={{ margin: '0 0 8px', color: D.sub, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</p>
      <p style={{ margin: 0, color: D.text, fontSize: 26, fontWeight: 700, letterSpacing: '-0.02em' }}>{value}</p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: dotColor }} />
        <span style={{ color: D.sub, fontSize: 11 }}>{caption}</span>
      </div>
    </div>
  );
}

function RecentTransactions({ recentTransactions, pendingDisputes, navigate, showExactValues, user }) {
  return (
    <section className="tt-card tt-glass" style={{ padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <h2 style={{ margin: 0, color: D.text, fontSize: 15, fontWeight: 700 }}>Recent transactions</h2>
        <button data-testid="view-all-transactions-link" onClick={() => navigate('/transactions')} className="tt-btn" style={{ display: 'flex', alignItems: 'center', gap: 5, border: 0, background: 'transparent', color: D.accent, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
          View all <ArrowRight size={11} />
        </button>
      </div>

      {recentTransactions.length === 0 ? (
        <RecentTransactionsEmptyState navigate={navigate} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {recentTransactions.map((transaction) => {
            // Role must be passed through so role-aware copy (e.g. payout
            // wording: "Funds Released" for the buyer vs "Payout Processing"
            // for the seller) matches what the dominant card already shows
            // for the same transaction — see buildActionItems in
            // dashboardMoneyState.js for the same pattern.
            const isBuyer = isUserBuyerForTransaction(transaction, user);
            const isSeller = isUserSellerForTransaction(transaction, user);
            const meta = resolveEscrowUiState(transaction, pendingDisputes, { role: isSeller ? 'seller' : isBuyer ? 'buyer' : undefined });
            const muted = isLikelyTestTransaction(transaction);
            const title = transaction.item_description || transaction.title || 'Protected transaction';
            return (
              <div
                key={transaction.transaction_id}
                data-testid={`transaction-row-${transaction.transaction_id}`}
                onClick={() => navigate(`/transactions/${transaction.transaction_id}`)}
                className="tt-row-hover tt-btn tt-tx-row"
                style={{ opacity: muted ? 0.55 : 1 }}
              >
                <div className="tt-tx-main">
                  <p style={{ margin: 0, color: D.text, fontWeight: 700, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {title}
                    {muted && (
                      <span style={{ marginLeft: 7, color: D.dim, border: `1px solid ${D.border}`, borderRadius: 999, padding: '1px 5px', fontSize: 9, fontWeight: 700, textTransform: 'uppercase' }}>Test</span>
                    )}
                  </p>
                  <p style={{ margin: '3px 0 0', color: D.sub, fontSize: 12.5 }}>{transaction.share_code || transaction.transaction_id}</p>
                </div>
                <div className="tt-tx-meta">
                  <span style={{ color: D.text, fontWeight: 800, fontSize: 15, flexShrink: 0 }}>{displayMoney(getTransactionValue(transaction), showExactValues, 2)}</span>
                  <span style={{ color: semanticTone(meta.color), background: `${meta.color}2E`, border: `1px solid ${meta.color}66`, borderRadius: 999, padding: '4px 10px', fontSize: 11.5, fontWeight: 700, flexShrink: 0, whiteSpace: 'nowrap' }}>
                    {meta.label}
                  </span>
                  <ArrowRight size={14} color={D.sub} className="tt-tx-arrow" style={{ flexShrink: 0 }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function RecentTransactionsEmptyState({ navigate }) {
  return (
    <div style={{ minHeight: 160, display: 'grid', placeItems: 'center', textAlign: 'center', border: `1px dashed ${D.border}`, borderRadius: 12, padding: 24 }}>
      <div>
        <ShieldCheck size={28} color={D.dim} style={{ margin: '0 auto 10px' }} />
        <p style={{ margin: '0 0 14px', color: D.sub, fontSize: 13 }}>No protected payments yet.</p>
        <button
          data-testid="empty-state-create-transaction"
          onClick={() => { trackStartTransaction({ source: 'dashboard_empty_state' }); navigate('/transactions/new'); }}
          className="tt-btn"
          style={{ border: 'none', background: D.accentGradient, color: '#061421', padding: '10px 16px', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 13 }}
        >
          Start your first transaction
        </button>
      </div>
    </div>
  );
}

function PaymentProtectionCard({ walletData, protectedValue, showExactValues, payoutSchedule }) {
  const hasWallet = Boolean(walletData);
  return (
    <section className="tt-card tt-glass" style={{ padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <div style={{ width: 32, height: 32, borderRadius: 9, display: 'grid', placeItems: 'center', background: `${D.success}1A`, border: `1px solid ${D.success}40` }}>
          <Lock size={15} color={D.success} />
        </div>
        <h3 style={{ margin: 0, color: D.text, fontSize: 14.5, fontWeight: 700 }}>Payment protection</h3>
      </div>
      <p style={{ margin: '0 0 2px', color: D.text, fontSize: 20, fontWeight: 700 }}>
        {hasWallet || protectedValue > 0 ? displayMoney(protectedValue, showExactValues, 2) : 'Nothing protected right now'}
      </p>
      <p style={{ margin: 0, color: D.sub, fontSize: 12.5, lineHeight: 1.5 }}>
        Protected with our payment provider until the agreed conditions are met. {payoutSchedule?.shortCopy || ''}
      </p>
    </section>
  );
}

function NeedHelpCard() {
  return (
    <section className="tt-card tt-glass" style={{ padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <div style={{ width: 32, height: 32, borderRadius: 9, display: 'grid', placeItems: 'center', background: `${D.accent}1A`, border: `1px solid ${D.accent}40` }}>
          <LifeBuoy size={15} color={D.accent} />
        </div>
        <h3 style={{ margin: 0, color: D.text, fontSize: 14.5, fontWeight: 700 }}>Need help?</h3>
      </div>
      <p style={{ margin: '0 0 14px', color: D.sub, fontSize: 12.5, lineHeight: 1.5 }}>
        Get guidance on a payment, dispute, or transaction — we're here.
      </p>
      <button
        onClick={() => window.dispatchEvent(new CustomEvent('trusttrade:open-support'))}
        className="tt-btn"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 7, border: `1px solid ${D.border}`, background: D.hover, color: D.text, borderRadius: 9, padding: '9px 14px', cursor: 'pointer', fontSize: 12.5, fontWeight: 600 }}
      >
        <Sparkles size={13} color={D.accent} />
        Open support
      </button>
    </section>
  );
}

export default Dashboard;
